import { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import PlusSubmitIcon, {
  RESULT_LINE_DURATION,
  RESULT_MOVE_DURATION,
} from '../Icons/PlusSubmitIcon';

const DEFAULT_TEXT = 'Absenden';
const RESULT_TEXTS = {
  success: 'Wir melden uns in kürze bei Ihnen.',
  error: 'Senden fehlgeschlagen. Erneut versuchen',
};
const MOVE_EASE = 'power3.inOut';
const TEXT_FADE_OUT_DURATION = 0.2;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SubmitButtonLabel = ({ status }) => {
  const [shownResult, setShownResult] = useState(null);
  const resultText = shownResult === status ? RESULT_TEXTS[status] : null;
  const iconRef = useRef(null);
  const iconTargetRefs = useRef({});
  const textRef = useRef(null);
  const timeline = useRef(null);
  const prevStatus = useRef(status);
  const iconFromLeft = useRef(null);

  if (prevStatus.current !== status) {
    prevStatus.current = status;
    iconFromLeft.current =
      iconRef.current?.getBoundingClientRect().left ?? null;
  }

  useLayoutEffect(() => {
    const icon = iconRef.current;
    const fromLeft = iconFromLeft.current;
    iconFromLeft.current = null;

    if (timeline.current) {
      timeline.current.kill();
    }
    gsap.set(icon, { x: 0 });
    gsap.set(textRef.current, { clearProps: 'opacity' });

    if (RESULT_TEXTS[status]) {
      const offset =
        iconTargetRefs.current[status].getBoundingClientRect().left -
        icon.getBoundingClientRect().left;

      if (prefersReducedMotion()) {
        setShownResult(status);
        return;
      }

      const swapAt = RESULT_LINE_DURATION + RESULT_MOVE_DURATION;
      timeline.current = gsap
        .timeline()
        .to(
          icon,
          { x: offset, duration: RESULT_MOVE_DURATION, ease: MOVE_EASE },
          RESULT_LINE_DURATION,
        )
        .to(
          textRef.current,
          { opacity: 0, duration: TEXT_FADE_OUT_DURATION, ease: 'none' },
          swapAt - TEXT_FADE_OUT_DURATION,
        )
        .call(() => setShownResult(status), null, swapAt);
    } else {
      setShownResult(null);

      const offset =
        fromLeft === null ? 0 : fromLeft - icon.getBoundingClientRect().left;
      if (offset && !prefersReducedMotion()) {
        timeline.current = gsap.fromTo(
          icon,
          { x: offset },
          { x: 0, duration: 0.5, ease: MOVE_EASE },
        );
      }
    }

    return () => {
      if (timeline.current) {
        timeline.current.kill();
      }
    };
  }, [status]);

  useLayoutEffect(() => {
    if (resultText) {
      gsap.set(iconRef.current, { x: 0 });
    }
  }, [resultText]);

  return (
    <span className='button-label-stack'>
      {Object.entries(RESULT_TEXTS).map(([result, text]) => (
        <span
          key={result}
          className='button-label-row button-label-sizer'
          aria-hidden='true'
        >
          <span>{text}</span>
          <span
            ref={(el) => {
              iconTargetRefs.current[result] = el;
            }}
            className='button-label-icon-space'
          />
        </span>
      ))}
      <span className='button-label-row'>
        <span
          key={resultText ? shownResult : 'default'}
          ref={textRef}
          className='button-label-fade'
        >
          {resultText || DEFAULT_TEXT}
        </span>
        <span ref={iconRef} className='button-label-icon'>
          <PlusSubmitIcon status={status} />
        </span>
      </span>
    </span>
  );
};

export default SubmitButtonLabel;
